/** @format */

import {
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	MenuItem,
	Pagination,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { useCallback, useEffect, useRef, useState } from "react";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Label from "../../label/Label";
import { InfoTile } from "./InvitationCard";
import RejectInvitationDialog from "./RejectInvitationDialog";
import { getInvitationStatusMeta } from "./invitationStatus";
import { colors } from "../../../utility/types/constant";
import {
	submitApproveCircleInvitation,
	submitFetchAdminInvitations,
	submitRejectCircleInvitation,
} from "../../../redux/services/circleInvitationService";
import { useToast } from "../../../providers/ToastProvider";

const FONT = "'Montserrat', sans-serif";
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const STATUS_ORDER = [
	"PENDING_INVITEE_ACTION",
	"ACCEPTED_AWAITING_VERIFICATION",
	"ACCEPTED_UNDER_REVIEW",
	"APPROVED_ADDING_MEMBER",
	"COMPLETED_MEMBER_ADDED",
	"DENIED",
	"REJECTED",
	"EXPIRED",
	"CANCELED",
	"INVALID",
];

const REJECTABLE = [
	"PENDING_INVITEE_ACTION",
	"ACCEPTED_AWAITING_VERIFICATION",
	"ACCEPTED_UNDER_REVIEW",
];

interface AdminInvitation {
	_id: string;
	circleId: string;
	status: string;
	sentBy: "leader" | "member";
	created_At: string;
	expiresAt: string;
	respondedAt: string | null;
	approvedAt: string | null;
	approvedByName: string | null;
	rejectedAt: string | null;
	rejectedByName: string | null;
	rejectionReason: string | null;
	circle: { _id: string; circleName: string; circleImage: string } | null;
	inviter: { name: string; email: string; isCircleLeader: boolean };
	invitee: {
		name: string;
		email: string;
		profileImage: string;
		hasAccount: boolean;
	};
}

interface AdminInvitationResponse {
	invitations: AdminInvitation[];
	total: number;
	totalPages: number;
	statusCounts: Record<string, number>;
	circleOptions: { _id: string; circleName: string }[];
}

const fadeUp = keyframes`
	0% { opacity: 0; transform: translateY(10px); }
	100% { opacity: 1; transform: translateY(0); }
`;

const formatDate = (value?: string | null) => {
	if (!value) return "—";
	const date = new Date(value);
	if (isNaN(date.getTime())) return "—";
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

const getErrorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message || error?.message || fallback;

const AdminInvitationCard = ({
	invitation,
	index,
	busyAction,
	isBusy,
	onApprove,
	onReject,
}: {
	invitation: AdminInvitation;
	index: number;
	busyAction: "approve" | "reject" | null;
	isBusy: boolean;
	onApprove: (invitation: AdminInvitation) => void;
	onReject: (invitation: AdminInvitation) => void;
}) => {
	const statusMeta = getInvitationStatusMeta(invitation.status);
	const name = invitation.invitee.name || invitation.invitee.email;
	const circleName = invitation.circle?.circleName || "Unknown Circle";
	const canApprove = invitation.status === "ACCEPTED_UNDER_REVIEW";
	const canReject = REJECTABLE.includes(invitation.status);
	const senderLabel =
		invitation.sentBy === "leader" ?
			invitation.inviter.isCircleLeader ? "Leader"
			:	"Admin"
		:	"Member";

	let outcome: { label: string; value: string; hint?: string } | null = null;
	if (invitation.status === "REJECTED") {
		outcome = {
			label: "Rejected",
			value: formatDate(invitation.rejectedAt),
			hint:
				[invitation.rejectedByName && `by ${invitation.rejectedByName}`, invitation.rejectionReason]
					.filter(Boolean)
					.join(" · ") || undefined,
		};
	} else if (invitation.approvedAt) {
		outcome = {
			label: "Approved",
			value: formatDate(invitation.approvedAt),
			hint: invitation.approvedByName ? `by ${invitation.approvedByName}` : undefined,
		};
	} else if (invitation.respondedAt) {
		outcome = { label: "Responded", value: formatDate(invitation.respondedAt) };
	}

	return (
		<Box
			component="article"
			aria-label={`Invitation for ${name} to join ${circleName}`}
			sx={{
				display: "flex",
				flexDirection: "column",
				borderRadius: "18px",
				backgroundColor: "#fff",
				border: "1px solid",
				borderColor:
					canApprove ? "rgba(20, 58, 162, 0.3)" : "rgba(15, 23, 42, 0.08)",
				boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
				animation: `${fadeUp} 0.35s ease-out both`,
				animationDelay: `${Math.min(index, 10) * 40}ms`,
				"@media (prefers-reduced-motion: reduce)": { animation: "none" },
			}}>
			<Box sx={{ p: { xs: 2, sm: 2.25 }, flex: 1 }}>
				<Stack direction="row" spacing={1.5} alignItems="flex-start">
					<Avatar
						alt={name}
						src={invitation.invitee.profileImage || undefined}
						sx={{
							width: 44,
							height: 44,
							fontWeight: 700,
							bgcolor: "rgba(20, 58, 162, 0.10)",
							color: colors.blueRibbon,
						}}>
						{name.charAt(0).toUpperCase()}
					</Avatar>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							noWrap
							sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "0.98rem" }}>
							{name}
						</Typography>
						<Typography
							noWrap
							sx={{ fontFamily: FONT, fontSize: "0.82rem", color: "text.secondary" }}>
							{invitation.invitee.email}
						</Typography>
						<Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
							<Label color={statusMeta.color}>{statusMeta.label}</Label>
							{!invitation.invitee.hasAccount && (
								<Label color="default" variant="outlined">
									No account yet
								</Label>
							)}
						</Box>
					</Box>
				</Stack>

				<Typography
					noWrap
					sx={{
						mt: 1.5,
						fontFamily: FONT,
						fontSize: "0.86rem",
						color: "text.secondary",
					}}>
					Circle:{" "}
					<Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
						{circleName}
					</Box>
				</Typography>

				<Box
					sx={{
						mt: 1.25,
						display: "grid",
						gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
						gap: 1,
					}}>
					<Box sx={{ gridColumn: "1 / -1" }}>
						<InfoTile
							icon={<PersonRoundedIcon />}
							label={`Sent by · ${senderLabel}`}
							value={invitation.inviter.name}
						/>
					</Box>
					<InfoTile
						icon={<SendRoundedIcon />}
						label="Sent"
						value={formatDate(invitation.created_At)}
					/>
					<InfoTile
						icon={<EventRoundedIcon />}
						label="Expires"
						value={formatDate(invitation.expiresAt)}
					/>
					{outcome && (
						<Box sx={{ gridColumn: "1 / -1" }}>
							<InfoTile
								icon={<HowToRegRoundedIcon />}
								label={outcome.label}
								value={outcome.value}
								hint={outcome.hint}
							/>
						</Box>
					)}
				</Box>
			</Box>

			{(canApprove || canReject) && (
				<Stack
					direction="row"
					spacing={1.25}
					sx={{ px: { xs: 2, sm: 2.25 }, pb: { xs: 2, sm: 2.25 } }}>
					{canReject && (
						<Button
							fullWidth
							variant="outlined"
							disabled={isBusy}
							onClick={() => onReject(invitation)}
							startIcon={busyAction === "reject" ? undefined : <CloseRoundedIcon />}
							sx={{
								minHeight: 44,
								borderRadius: "12px",
								textTransform: "none",
								fontFamily: FONT,
								fontWeight: 700,
								color: colors.persianRed,
								borderColor: "rgba(202, 52, 51, 0.45)",
								"&:hover": {
									borderColor: colors.persianRed,
									backgroundColor: "rgba(202, 52, 51, 0.06)",
								},
							}}>
							{busyAction === "reject" ?
								<CircularProgress size={20} sx={{ color: colors.persianRed }} />
							:	"Reject"}
						</Button>
					)}
					{canApprove && (
						<Button
							fullWidth
							variant="contained"
							disableElevation
							disabled={isBusy}
							onClick={() => onApprove(invitation)}
							startIcon={
								busyAction === "approve" ? undefined : <HowToRegRoundedIcon />
							}
							sx={{
								minHeight: 44,
								borderRadius: "12px",
								textTransform: "none",
								fontFamily: FONT,
								fontWeight: 700,
								color: "#fff",
								backgroundColor: colors.secondPrimary,
								"&:hover": { backgroundColor: "#03462f" },
							}}>
							{busyAction === "approve" ?
								<CircularProgress size={20} sx={{ color: "#fff" }} />
							:	"Approve"}
						</Button>
					)}
				</Stack>
			)}
		</Box>
	);
};

/**
 * Admin-only oversight of every circle invitation (all circles, all statuses,
 * leader- or member-sent), with approve/reject actions.
 */
const AdminAllInvitations = () => {
	const toast = useToast();
	const [openNav, setOpenNav] = useState(false);

	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState("ALL");
	const [circleId, setCircleId] = useState("");
	const [sentBy, setSentBy] = useState("all");
	const [page, setPage] = useState(1);

	const [data, setData] = useState<AdminInvitationResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState("");
	const [busy, setBusy] = useState<{ id: string; action: "approve" | "reject" } | null>(
		null,
	);
	const [approveTarget, setApproveTarget] = useState<AdminInvitation | null>(null);
	const [isApproveOpen, setIsApproveOpen] = useState(false);
	const [rejectTarget, setRejectTarget] = useState<AdminInvitation | null>(null);
	const [isRejectOpen, setIsRejectOpen] = useState(false);
	const [reloadKey, setReloadKey] = useState(0);
	const listTopRef = useRef<HTMLDivElement>(null);

	// Debounce the search box (only when the text actually changed)
	useEffect(() => {
		if (searchInput === search) return;
		const handle = setTimeout(() => {
			setSearch(searchInput);
			setPage(1);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [searchInput, search]);

	// Fetch whenever the query changes; abort the previous request so a slow
	// response can never overwrite a newer one.
	useEffect(() => {
		const controller = new AbortController();
		setIsLoading(true);
		setLoadError("");

		submitFetchAdminInvitations(
			{ page, limit: PAGE_SIZE, status, circleId, sentBy, search },
			controller.signal,
		)
			.then((result: AdminInvitationResponse) => {
				setData(result);
				// Clamp if the current page no longer exists (e.g. after rejecting the last item)
				if (result.totalPages < page) setPage(result.totalPages);
			})
			.catch((error: any) => {
				if (controller.signal.aborted || error?.name === "CanceledError") return;
				setLoadError(getErrorMessage(error, "Failed to load invitations."));
			})
			.finally(() => {
				if (!controller.signal.aborted) setIsLoading(false);
			});

		return () => controller.abort();
	}, [page, status, circleId, sentBy, search, reloadKey]);

	const reload = useCallback(() => setReloadKey((k) => k + 1), []);

	const resetToFirstPage = <T,>(setter: (value: T) => void) => (value: T) => {
		setter(value);
		setPage(1);
	};

	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
		listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const runAction = async (
		invitation: AdminInvitation,
		action: "approve" | "reject",
		reason?: string,
	) => {
		setBusy({ id: invitation._id, action });
		try {
			const result =
				action === "approve" ?
					await submitApproveCircleInvitation(invitation._id)
				:	await submitRejectCircleInvitation(invitation._id, reason || undefined);
			toast.success(
				result?.message ||
					(action === "approve" ? "Invitation approved." : "Invitation rejected."),
			);
		} catch (error: any) {
			toast.error(
				getErrorMessage(
					error,
					action === "approve" ?
						"Failed to approve the invitation."
					:	"Failed to reject the invitation.",
				),
				{ title: action === "approve" ? "Couldn't approve" : "Couldn't reject" },
			);
		} finally {
			setBusy(null);
			// Statuses and counts changed either way; resync from the server.
			reload();
		}
	};

	const statusCounts = data?.statusCounts ?? {};
	const allCount = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);
	const statusChips = [
		{ value: "ALL", label: "All", count: allCount },
		...STATUS_ORDER.filter((s) => (statusCounts[s] ?? 0) > 0 || s === status).map(
			(s) => ({
				value: s,
				label: getInvitationStatusMeta(s).label,
				count: statusCounts[s] ?? 0,
			}),
		),
	];

	const invitations = data?.invitations ?? [];
	const totalPages = data?.totalPages ?? 1;
	const total = data?.total ?? 0;
	const pageStart = (page - 1) * PAGE_SIZE;

	const selectSx = {
		minWidth: { xs: "100%", sm: 200 },
		"& .MuiOutlinedInput-root": { borderRadius: "12px", fontFamily: FONT },
	};

	return (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				<Container
					maxWidth="xl"
					sx={{ mt: { xs: 11, lg: 12 }, pb: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
					{/* Header */}
					<Stack
						direction="row"
						alignItems="flex-start"
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 2.5 }}>
						<Box>
							<Typography
								component="h1"
								sx={{
									fontFamily: FONT,
									fontSize: { xs: "1.35rem", sm: "1.6rem" },
									fontWeight: 800,
								}}>
								All Invitations
							</Typography>
							<Typography
								sx={{
									fontFamily: FONT,
									fontSize: { xs: "0.88rem", sm: "0.95rem" },
									color: "text.secondary",
									mt: 0.5,
								}}>
								Every circle invitation sent by leaders and members, across all
								circles.
							</Typography>
						</Box>
						<Button
							onClick={reload}
							disabled={isLoading}
							startIcon={
								isLoading ? <CircularProgress size={16} /> : <RefreshRoundedIcon />
							}
							sx={{
								flexShrink: 0,
								minHeight: 40,
								borderRadius: "10px",
								textTransform: "none",
								fontFamily: FONT,
								fontWeight: 700,
								color: colors.blueRibbon,
							}}>
							Refresh
						</Button>
					</Stack>

					{/* Filters */}
					<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
						<TextField
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							placeholder="Search invitee name or email"
							size="small"
							fullWidth
							inputProps={{ "aria-label": "Search invitations", maxLength: 100 }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchRoundedIcon fontSize="small" />
									</InputAdornment>
								),
								sx: { borderRadius: "12px", fontFamily: FONT, minHeight: 44 },
							}}
						/>
						<TextField
							select
							size="small"
							label="Circle"
							value={circleId}
							onChange={(e) => resetToFirstPage(setCircleId)(e.target.value)}
							sx={selectSx}>
							<MenuItem value="">All circles</MenuItem>
							{(data?.circleOptions ?? []).map((c) => (
								<MenuItem key={c._id} value={c._id}>
									{c.circleName}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							size="small"
							label="Sent by"
							value={sentBy}
							onChange={(e) => resetToFirstPage(setSentBy)(e.target.value)}
							sx={selectSx}>
							<MenuItem value="all">Anyone</MenuItem>
							<MenuItem value="leader">Leader or admin</MenuItem>
							<MenuItem value="member">Member</MenuItem>
						</TextField>
					</Stack>

					<Stack
						ref={listTopRef}
						direction="row"
						spacing={1}
						sx={{
							mb: 2.5,
							overflowX: "auto",
							pb: 0.5,
							scrollMarginTop: { xs: 88, lg: 104 },
							"&::-webkit-scrollbar": { display: "none" },
						}}>
						{statusChips.map((chip) => {
							const selected = status === chip.value;
							return (
								<Chip
									key={chip.value}
									label={`${chip.label} (${chip.count})`}
									onClick={() => resetToFirstPage(setStatus)(chip.value)}
									sx={{
										flexShrink: 0,
										height: 36,
										borderRadius: "10px",
										fontFamily: FONT,
										fontWeight: 700,
										color: selected ? "#fff" : "text.secondary",
										backgroundColor:
											selected ? colors.secondPrimary : "rgba(15, 23, 42, 0.05)",
										"&:hover": {
											backgroundColor:
												selected ? "#03462f" : "rgba(15, 23, 42, 0.09)",
										},
									}}
								/>
							);
						})}
					</Stack>

					{loadError && (
						<Box
							role="alert"
							sx={{
								mb: 2,
								p: 1.5,
								borderRadius: "12px",
								color: colors.persianRed,
								backgroundColor: "rgba(202, 52, 51, 0.06)",
								fontFamily: FONT,
								fontSize: "0.88rem",
							}}>
							{loadError}
						</Box>
					)}

					{/* Results */}
					{isLoading && !data ?
						<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
							<CircularProgress />
						</Box>
					: invitations.length > 0 ?
						<Box
							sx={{
								display: "grid",
								gap: 2,
								opacity: isLoading ? 0.6 : 1,
								transition: "opacity 0.2s ease",
								gridTemplateColumns: {
									xs: "1fr",
									md: "repeat(2, minmax(0, 1fr))",
									xl: "repeat(3, minmax(0, 1fr))",
								},
							}}>
							{invitations.map((invitation, index) => (
								<AdminInvitationCard
									key={invitation._id}
									invitation={invitation}
									index={index}
									busyAction={busy?.id === invitation._id ? busy.action : null}
									isBusy={busy !== null}
									onApprove={(inv) => {
										if (busy) return;
										setApproveTarget(inv);
										setIsApproveOpen(true);
									}}
									onReject={(inv) => {
										if (busy) return;
										setRejectTarget(inv);
										setIsRejectOpen(true);
									}}
								/>
							))}
						</Box>
					:	<Card
							sx={{
								py: 6,
								px: 3,
								textAlign: "center",
								borderRadius: "20px",
								boxShadow: "none",
								border: "1px dashed rgba(15, 23, 42, 0.15)",
							}}>
							<InboxRoundedIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1 }} />
							<Typography sx={{ fontFamily: FONT, fontWeight: 700 }}>
								No invitations match these filters
							</Typography>
						</Card>
					}

					{totalPages > 1 && (
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems="center"
							justifyContent="space-between"
							spacing={1.5}
							sx={{ mt: 3 }}>
							<Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "text.secondary" }}>
								Showing {total === 0 ? 0 : pageStart + 1}–
								{pageStart + invitations.length} of {total}
							</Typography>
							<Pagination
								page={Math.min(page, totalPages)}
								count={totalPages}
								onChange={handlePageChange}
								shape="rounded"
								siblingCount={0}
								boundaryCount={1}
								sx={{
									"& .MuiPaginationItem-root": {
										minWidth: 40,
										height: 40,
										borderRadius: "10px",
										fontFamily: FONT,
										fontWeight: 700,
									},
									"& .MuiPaginationItem-root.Mui-selected": {
										color: "#fff",
										backgroundColor: colors.secondPrimary,
										"&:hover": { backgroundColor: "#03462f" },
									},
								}}
							/>
						</Stack>
					)}
				</Container>
			</Box>

			{/* Approve confirmation */}
			<Dialog
				open={isApproveOpen}
				onClose={() => setIsApproveOpen(false)}
				TransitionProps={{ onExited: () => setApproveTarget(null) }}
				fullWidth
				maxWidth="xs"
				PaperProps={{ sx: { borderRadius: "20px", m: 2 } }}>
				<DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, pb: 1 }}>
					Add {approveTarget?.invitee.name || "this person"} to the circle?
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontFamily: FONT, color: "text.secondary", lineHeight: 1.6 }}>
						They'll become a member of{" "}
						<strong>{approveTarget?.circle?.circleName || "this circle"}</strong>. If a
						contribution cycle is scheduled but hasn't started yet, they'll get a payout
						slot and start contributing when it begins.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
					<Button
						onClick={() => setIsApproveOpen(false)}
						sx={{
							minHeight: 44,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							color: "text.secondary",
						}}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disableElevation
						onClick={() => {
							setIsApproveOpen(false);
							if (approveTarget) runAction(approveTarget, "approve");
						}}
						sx={{
							minHeight: 44,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							backgroundColor: colors.secondPrimary,
							"&:hover": { backgroundColor: "#03462f" },
						}}>
						Approve
					</Button>
				</DialogActions>
			</Dialog>

			<RejectInvitationDialog
				open={isRejectOpen}
				inviteeName={rejectTarget?.invitee.name || "This person"}
				circleName={rejectTarget?.circle?.circleName || "this circle"}
				onCancel={() => setIsRejectOpen(false)}
				onConfirm={(reason) => {
					setIsRejectOpen(false);
					if (rejectTarget) runAction(rejectTarget, "reject", reason);
				}}
				onExited={() => setRejectTarget(null)}
			/>
		</>
	);
};

export default AdminAllInvitations;
